import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  Copy,
  X as XIcon,
  Twitter,
  Instagram,
  MessageCircle,
  Share2,
  Loader2,
  Check,
} from "lucide-react";

/**
 * Share modal — copy link + Twitter intent + native Web Share + Instagram (copy fallback).
 * Pass `handle` (required), `name` (optional display name), and optionally
 * `badge` if sharing a specific achievement: { id, title, icon }.
 */
export default function ShareProfileModal({ handle, name, badge, onClose }) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  const url = useMemo(() => {
    const base = window.location.origin.replace(/\/$/, "");
    const path = `/u/${encodeURIComponent(handle)}`;
    return base + path;
  }, [handle]);

  const message = useMemo(() => {
    if (badge) {
      return `I just unlocked ${badge.icon} ${badge.title} on @OmniMuscle_Bodybuilding 🐦‍🔥 — Flying Higher.`;
    }
    return `Check out ${name ? name + " (" : ""}@${handle}${name ? ")" : ""} on OMNIMUSCLE — Holistic Healing · Flying Higher 🐦‍🔥`;
  }, [handle, name, badge]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${message}\n${url}`);
      setCopied(true);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopied(false), 2400);
    } catch {
      toast.error("Copy failed — select the link manually.");
    }
  };

  const tweet = () => {
    const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(url)}`;
    window.open(intent, "_blank", "noopener,noreferrer");
  };

  const native = async () => {
    if (!navigator.share) {
      copy();
      return;
    }
    setSharing(true);
    try {
      await navigator.share({ title: "OMNIMUSCLE", text: message, url });
    } catch (e) {
      // user canceled — ignore
    } finally {
      setSharing(false);
    }
  };

  const insta = () => {
    // Instagram has no native web share intent. Copy + open IG in a new tab.
    copy();
    toast.info("Link copied — paste it into your Instagram story or bio.", { duration: 4500 });
    window.open("https://instagram.com", "_blank", "noopener,noreferrer");
  };

  // Auto-focus the URL input
  useEffect(() => {
    const t = setTimeout(() => {
      const i = document.querySelector('[data-testid="share-url-input"]');
      if (i) {
        i.focus();
        i.select?.();
      }
    }, 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      data-testid="share-modal"
      className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              {badge ? "Share Badge" : "Share Profile"}
            </div>
            <h3 className="font-display text-2xl text-white leading-tight">
              {badge ? (
                <>
                  {badge.icon} {badge.title}
                </>
              ) : (
                <>@{handle}</>
              )}
            </h3>
          </div>
          <button
            data-testid="share-close-btn"
            onClick={onClose}
            className="text-neutral-500 hover:text-white"
          >
            <XIcon size={22} />
          </button>
        </div>

        <p className="text-sm text-neutral-400 mb-4 leading-relaxed">{message}</p>

        <div className="flex gap-2 mb-4">
          <input
            data-testid="share-url-input"
            readOnly
            value={url}
            className="input-omni flex-1 text-sm"
          />
          <button
            data-testid="share-copy-btn"
            onClick={copy}
            className={`btn-omni ${copied ? "btn-red" : "btn-outline-gold"} flex-shrink-0`}
            title="Copy link"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <button
            data-testid="share-twitter-btn"
            onClick={tweet}
            className="py-3 bg-[#0A0A0A] border-2 border-zinc-800 hover:border-sky-400 hover:bg-sky-400/5 transition-colors flex flex-col items-center gap-1"
          >
            <Twitter size={18} className="text-sky-400" />
            <span className="text-[10px] uppercase tracking-widest text-neutral-400">Twitter</span>
          </button>
          <button
            data-testid="share-insta-btn"
            onClick={insta}
            className="py-3 bg-[#0A0A0A] border-2 border-zinc-800 hover:border-pink-400 hover:bg-pink-400/5 transition-colors flex flex-col items-center gap-1"
          >
            <Instagram size={18} className="text-pink-400" />
            <span className="text-[10px] uppercase tracking-widest text-neutral-400">Instagram</span>
          </button>
          {navigator.share ? (
            <button
              data-testid="share-native-btn"
              onClick={native}
              disabled={sharing}
              className="py-3 bg-[#0A0A0A] border-2 border-zinc-800 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-colors flex flex-col items-center gap-1 disabled:opacity-50"
            >
              {sharing ? <Loader2 className="animate-spin text-[#D4AF37]" size={18} /> : <Share2 size={18} className="text-[#D4AF37]" />}
              <span className="text-[10px] uppercase tracking-widest text-neutral-400">More</span>
            </button>
          ) : (
            <button
              data-testid="share-message-btn"
              onClick={() => {
                window.open(`sms:?body=${encodeURIComponent(message + " " + url)}`, "_blank");
              }}
              className="py-3 bg-[#0A0A0A] border-2 border-zinc-800 hover:border-emerald-400 hover:bg-emerald-400/5 transition-colors flex flex-col items-center gap-1"
            >
              <MessageCircle size={18} className="text-emerald-400" />
              <span className="text-[10px] uppercase tracking-widest text-neutral-400">SMS</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
